import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

/** Mirrors `MAX_NAME` in the API's report_labels validators. */
export const MAX_LABEL_NAME = 60;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  /** What the box starts with — the current name when renaming, else empty. */
  initialName: string;
  submitLabel: string;
  /** The server's refusal, shown under the field (G12). */
  error: string | null;
  busy: boolean;
  onSubmit: (name: string) => void;
}

/**
 * Name a label, or rename one.
 *
 * Stays open on a refusal and shows it under the box: a taken name is the
 * likeliest one, and the fix is to edit what is already on screen.
 */
export function LabelNameDialog({
  open,
  onOpenChange,
  title,
  description,
  initialName,
  submitLabel,
  error,
  busy,
  onSubmit,
}: Props) {
  const [name, setName] = useState(initialName);

  // Re-seeded when the dialog opens, and on nothing else.
  const seed = useRef(initialName);
  seed.current = initialName;
  useEffect(() => {
    if (open) setName(seed.current);
  }, [open]);

  const trimmed = name.trim();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (trimmed) onSubmit(trimmed);
          }}
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="report-label-name">Name</Label>
            <Input
              id="report-label-name"
              autoFocus
              value={name}
              maxLength={MAX_LABEL_NAME}
              onChange={(e) => setName(e.target.value)}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? 'report-label-name-error' : undefined}
            />
            {error ? (
              <p id="report-label-name-error" role="alert" className="text-destructive text-sm">
                {error}
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!trimmed || busy}>
              {busy ? 'Saving…' : submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
